import React, { useState, useEffect } from "react";
import { UserInfoModel } from "../services/users/user.api.interface";
import usersApi from "../services/users/UserApi";
import { useNavigate } from "react-router-dom";
import CommonHeader from "./CommonHeader";
import ToastNotification, { showToast } from "./Toaster";
import CustomDropdown from "./CustomDropdown";
import PillButton from "./button/Pills";

const UsersList = () => {
  const [userList, setUserList] = useState<UserInfoModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<UserInfoModel[]>([]);
  const [filterStatus, setFilterStatus] = useState<string>("active");
  const navigate = useNavigate();

  const fetchUserList = async (status: string) => {
    setLoading(true);
    try {
      const response = await usersApi.getUserList(1, status);
      if (response.success && response.value && Array.isArray(response.value.data.contents)) {
        const users = response.value.data.contents.map((user: any) => ({
          id: user.id,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email,
          phoneNumber: user.phoneNumber || "",
          status: user.status || "pending",
        }));
        setUserList(users);
      } else {
        console.error("Failed to fetch users:", response || "Unknown error");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserList(filterStatus);
  }, [filterStatus]);

  const handleFilterChange = (status: string) => {
    setFilterStatus(status);
  };

  const handleDeleteClick = (user: UserInfoModel) => {
    setSelectedUsers([user]);
    setOpenDeleteModal(true);
  };

  const handleUserSelection = (user: UserInfoModel) => {
    setSelectedUsers((prevSelected) => {
      if (prevSelected.some((u) => u.id === user.id)) {
        return prevSelected.filter((u) => u.id !== user.id);
      } else {
        return [...prevSelected, user];
      }
    });
  };

  const handleDeleteSelectedUsers = async () => {
    setOpenDeleteModal(true);
  };

  const confirmDeleteSelectedUsers = async () => {
    try {
      const userIds = selectedUsers.map((user) => user.id + "");
      const response = await usersApi.deleteUser(userIds);
      if (response.success) {
        setUserList((prevList) =>
          prevList.filter((u) => !userIds.includes(u.id.toString()))
        );
        setSelectedUsers([]);
        setOpenDeleteModal(false);
        showToast("Users deleted successfully!", "success");
      } else {
        console.error("Failed to delete users:", response.message);
      }
    } catch (error) {
      console.error("Error deleting users:", error);
    }
  };

  const getUserConfig = async (userId: number) => {
    try {
      const response = await usersApi.getUserConfig(userId);
      if (response.success) {
        navigate(`/superadmin/user/config/${userId}`);
      } else {
        console.error("Failed to fetch user config:", response.error.message);
      }
    } catch (error) {
      console.error("Error fetching user config:", error);
    }
  };

  return (
    <>
      <div className="flex w-full h-[calc(100vh-67px)]">
        <ToastNotification />
        <div className="flex-1">
          <CommonHeader title="User List" />

          {loading ? (
            <p className="text-ez-text-muted text-center mt-[200px]">Loading...</p>
          ) : (
            <div className="w-full px-16 py-6">
              <div className="relative overflow-x-auto h-[calc(100vh-267px)]">
                <div className="flex justify-end items-center h-[45px] mb-1 gap-2">
                  <CustomDropdown value={filterStatus} onChange={handleFilterChange} />
                  {selectedUsers.length >= 1 ? (
                    <PillButton
                      variant="danger"
                      size="sm"
                      disabled={selectedUsers.length < 1}
                      onClick={handleDeleteSelectedUsers}
                    >
                      Delete ({selectedUsers.length})
                    </PillButton>
                  ) : (
                    <div className="h-[38px]" />
                  )}
                </div>
                <div className="relative overflow-x-auto border border-ez-border bg-ez-surface shadow-card">
                  <table className="w-full text-sm text-left text-ez-text-primary">
                    <thead className="text-xs uppercase bg-ez-elevated text-ez-text-secondary">
                      <tr>
                        <th scope="col" className="px-6 py-3">
                          <input
                            type="checkbox"
                            onChange={(e) =>
                              setSelectedUsers(
                                e.target.checked ? [...userList] : []
                              )
                            }
                            checked={
                              selectedUsers.length === userList.length &&
                              userList.length > 0
                            }
                          />
                        </th>
                        <th scope="col" className="px-6 py-3">Name</th>
                        <th scope="col" className="px-6 py-3">Email</th>
                        <th scope="col" className="px-6 py-3">Phone Number</th>
                        <th scope="col" className="px-6 py-3">Status</th>
                        <th scope="col" className="px-6 py-3">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userList.map((user) => (
                        <tr key={user.id} className="border-b border-ez-border dark:border-ez-border odd:bg-white dark:odd:bg-ez-surface even:bg-gray-50 dark:even:bg-slate-700/40 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors">
                          <td className="px-6 py-4">
                            <input
                              type="checkbox"
                              checked={selectedUsers.some((u) => u.id === user.id)}
                              onChange={() => handleUserSelection(user)}
                            />
                          </td>
                          <th scope="row" className="px-6 py-4 font-medium text-ez-text-primary whitespace-nowrap capitalize">
                            {user.firstName} {user.lastName}
                          </th>
                          <td className="px-6 py-4 text-ez-text-secondary">{user.email}</td>
                          <td className="px-6 py-4 text-ez-text-secondary">{user.phoneNumber}</td>
        <td className="px-6 py-4 capitalize text-green-600 dark:text-green-400 font-semibold">
                            {user.status}
                          </td>
                          <td className="px-6 py-4">
                            <button
                              type="button"
                              className="action-btn"
                              onClick={() => handleDeleteClick(user)}
                            >
                              <svg
                                stroke="currentColor"
                                fill="currentColor"
                                strokeWidth="0"
                                viewBox="0 0 448 512"
                                height="1em"
                                width="1em"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"></path>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="action-btn ml-2"
                              onClick={() => getUserConfig(user.id)}
                            >
                              <svg
                                stroke="currentColor"
                                fill="currentColor"
                                strokeWidth="0"
                                viewBox="0 0 512 512"
                                height="1em"
                                width="1em"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M487.4 315.7l-42.6-24.6c4.3-23.2 4.3-47 0-70.2l42.6-24.6c4.9-2.8 7.1-8.6 5.5-14-11.1-35.6-30-67.8-54.7-94.6-3.8-4.1-10-5.1-14.8-2.3L380.8 110c-17.9-15.4-38.5-27.3-60.8-35.1V25.8c0-5.6-3.9-10.5-9.4-11.7-36.7-8.2-74.3-7.8-109.2 0-5.5 1.2-9.4 6.1-9.4 11.7V75c-22.2 7.9-42.8 19.8-60.8 35.1L88.7 85.5c-4.9-2.8-11-1.9-14.8 2.3-24.7 26.7-43.6 58.9-54.7 94.6-1.7 5.4.6 11.2 5.5 14L67.3 221c-4.3 23.2-4.3 47 0 70.2l-42.6 24.6c-4.9 2.8-7.1 8.6-5.5 14 11.1 35.6 30 67.8 54.7 94.6 3.8 4.1 10 5.1 14.8 2.3l42.6-24.6c17.9 15.4 38.5 27.3 60.8 35.1v49.2c0 5.6 3.9 10.5 9.4 11.7 36.7 8.2 74.3 7.8 109.2 0 5.5-1.2 9.4-6.1 9.4-11.7v-49.2c22.2-7.9 42.8-19.8 60.8-35.1l42.6 24.6c4.9 2.8 11 1.9 14.8-2.3 24.7-26.7 43.6-58.9 54.7-94.6 1.5-5.5-.7-11.3-5.6-14.1zM256 336c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z"></path>
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                      {userList && userList.length === 0 ? (
                        <tr className="text-ez-text-muted text-center">
                          <td className="py-4 font-semibold" colSpan={6}>
                            No records found.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {openDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm"
          onClick={() => setOpenDeleteModal(false)}
        >
          <div className="bg-ez-surface border border-ez-border rounded-xl shadow-2xl w-full max-w-sm mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-ez-border">
              <h3 className="text-base font-semibold text-ez-text-primary">Delete User(s)</h3>
              <button
                onClick={() => setOpenDeleteModal(false)}
                className="text-ez-text-muted hover:text-ez-text-primary transition-colors"
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="p-6 text-center">
              <p className="text-sm text-ez-text-secondary mb-6">
                Are you sure you want to delete the selected user(s)? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <PillButton variant="secondary" size="md" onClick={() => setOpenDeleteModal(false)}>
                  Cancel
                </PillButton>
                <PillButton variant="danger" size="md" onClick={confirmDeleteSelectedUsers}>
                  Delete
                </PillButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UsersList;